import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { api, apiError, inr, logError, uploadFile } from "@/lib/api";
import { LostLeadDialog } from "@/components/LostLead";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { SecureExportDialog } from "@/components/SecureExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  title: "", customer_id: "", customer_name: "", contact_person: "", mobile: "",
  requirement: "", product_interest: "", estimated_value: 0, expected_close: "",
  probability: 30, source: "Referral", stage: "New", notes: "",
};

export default function Leads() {
  const { can } = useAuth();
  const [stages, setStages] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dragId, setDragId] = useState(null);
  const [hotOnly, setHotOnly] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [lostLead, setLostLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setStages(data.stages);
      setItems(data.items);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .get("/customers?limit=200")
      .then(({ data }) => setCustomers(data.items))
      .catch((e) => logError("customer list", e));
  }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    stages.forEach((s) => (g[s] = []));
    (hotOnly ? items.filter((i) => i.temperature === "Hot") : items).forEach((i) => {
      (g[i.stage] = g[i.stage] || []).push(i);
    });
    return g;
  }, [items, stages, hotOnly]);

  const pickFiles = (list) => setAttachments((x) => [...x, ...Array.from(list)]);

  const save = async () => {
    try {
      if (!form.title.trim()) return toast.error("Enter a lead title");
      const { data: lead } = await api.post("/leads", {
        ...form, estimated_value: Number(form.estimated_value),
      });
      for (const f of attachments) {
        try {
          await uploadFile(f, "lead", lead.id);
        } catch (err) {
          toast.error(`${f.name}: ${apiError(err)}`);
        }
      }
      toast.success(attachments.length ? `Lead created • ${attachments.length} file(s) filed in Leads vault` : "Lead created");
      setOpen(false);
      setForm(EMPTY);
      setAttachments([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const move = async (id, stage) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, stage } : i)));
    try {
      await api.patch(`/leads/${id}/stage`, { stage });
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  const totalValue = items.reduce((s, i) => s + Number(i.estimated_value || 0), 0);
  const hotCount = items.filter((i) => i.temperature === "Hot").length;

  return (
    <div>
      <PageHeader
        testId="leads-header"
        title="Lead Pipeline"
        subtitle={`${items.length} open leads • ${inr(totalValue)} estimated value • ${hotCount} hot`}
        actions={
          <>
            <Button size="sm" variant="outline" className="gap-1.5"
              data-testid="leads-export-button" onClick={() => setExportOpen(true)}>
              Export (Owner OTP)
            </Button>
            <Button
              size="sm"
              variant={hotOnly ? "default" : "outline"}
              className="gap-1.5"
              data-testid="hot-only-toggle"
              onClick={() => setHotOnly((v) => !v)}
            >
              🔥 Hot only ({hotCount})
            </Button>
            {can("leads", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-lead-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>New lead</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Lead title" className="sm:col-span-2">
                    <Input
                      data-testid="lead-title-input"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                  </Field>
                  <Field label="Customer" className="sm:col-span-2">
                    <Select
                      value={form.customer_id}
                      onValueChange={(v) => {
                        const c = customers.find((x) => x.id === v);
                        setForm({
                          ...form, customer_id: v, customer_name: c?.company || "",
                          contact_person: c?.contact_person || "", mobile: c?.mobile || "",
                        });
                      }}
                    >
                      <SelectTrigger data-testid="lead-customer-select">
                        <SelectValue placeholder="Select existing customer (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Contact person">
                    <Input
                      value={form.contact_person}
                      onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                    />
                  </Field>
                  <Field label="Mobile">
                    <Input
                      data-testid="lead-mobile-input"
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    />
                  </Field>
                  <Field label="Estimated value (₹)">
                    <Input
                      data-testid="lead-value-input"
                      type="number"
                      value={form.estimated_value}
                      onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
                    />
                  </Field>
                  <Field label="Expected close">
                    <Input
                      type="date"
                      value={form.expected_close}
                      onChange={(e) => setForm({ ...form, expected_close: e.target.value })}
                    />
                  </Field>
                  <Field label="Stage">
                    <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                      <SelectTrigger data-testid="lead-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Source">
                    <Input
                      value={form.source}
                      onChange={(e) => setForm({ ...form, source: e.target.value })}
                    />
                  </Field>
                  <Field label="Requirement" className="sm:col-span-2">
                    <Textarea
                      data-testid="lead-requirement-input"
                      value={form.requirement}
                      onChange={(e) => setForm({ ...form, requirement: e.target.value })}
                    />
                  </Field>
                  <div className="rounded border border-dashed border-border p-3 sm:col-span-2">
                    <div className="mb-1 text-xs font-medium text-muted-foreground">
                      Visiting card / enquiry documents — filed in the Leads vault folder
                    </div>
                    <input type="file" multiple className="w-full text-xs"
                      accept="image/*,application/pdf,.doc,.docx,.csv,.xlsx"
                      data-testid="lead-file-input"
                      onChange={(e) => e.target.files?.length && pickFiles(e.target.files)} />
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-accent">
                      <input type="file" accept="image/*" capture="environment" multiple hidden
                        data-testid="lead-camera-input"
                        onChange={(e) => e.target.files?.length && pickFiles(e.target.files)} />
                      Snap with camera
                    </label>
                    {attachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1" data-testid="lead-attached-files">
                        {attachments.map((f) => (
                          <Badge key={f.name} variant="secondary" className="text-[10px]">
                            {f.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="lead-save-button" onClick={save}>
                    Create lead
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </>
        }
      />
      <SecureExportDialog entity="leads" open={exportOpen} onOpenChange={setExportOpen} />

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto p-4 sm:p-6" data-testid="leads-kanban">
          {stages.map((stage) => (
            <div
              key={stage}
              data-testid={`kanban-column-${stage.toLowerCase().replace(/\s+/g, "-")}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dragId && move(dragId, stage)}
              className="flex w-64 shrink-0 flex-col rounded-lg border border-border bg-secondary/40"
            >
              <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wider">{stage}</span>
                <Badge variant="outline" className="font-mono-num text-[10px]">
                  {(grouped[stage] || []).length}
                </Badge>
              </div>
              <div className="flex-1 space-y-2 p-2">
                {(grouped[stage] || []).map((l) => (
                  <div
                    key={l.id}
                    data-testid={`lead-card-${l.id}`}
                    draggable
                    onDragStart={() => setDragId(l.id)}
                    onDragEnd={() => setDragId(null)}
                    className="cursor-grab rounded-md border border-border bg-card p-3 shadow-sm transition-transform hover:-translate-y-0.5"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium leading-snug">{l.title}</div>
                      {l.temperature === "Hot" && (
                        <span
                          data-testid={`lead-hot-badge-${l.id}`}
                          title={(l.score_reasons || []).join(" • ")}
                          className="badge-hot shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                        >
                          🔥 HOT {l.score}
                        </span>
                      )}
                      {l.temperature === "Warm" && (
                        <span
                          data-testid={`lead-warm-badge-${l.id}`}
                          title={(l.score_reasons || []).join(" • ")}
                          className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold text-amber-600"
                        >
                          WARM {l.score}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {l.customer_name || l.contact_person || "—"}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono-num text-xs text-accent">
                        {inr(l.estimated_value)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{l.probability}%</span>
                    </div>
                    {can("leads", "edit") && (
                      <Select value={l.stage} onValueChange={(v) => move(l.id, v)}>
                        <SelectTrigger
                          data-testid={`lead-stage-change-${l.id}`}
                          className="mt-2 h-7 text-[11px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map((s) => (
                            <SelectItem key={s} value={s} className="text-xs">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {can("leads", "edit") && l.stage !== "Lost" && (
                      <Button size="sm" variant="ghost"
                        className="mt-1 h-6 w-full text-[10px]"
                        data-testid={`lead-lost-button-${l.id}`}
                        onClick={() => setLostLead(l)}>
                        Mark lost
                      </Button>
                    )}
                  </div>
                ))}
                {(grouped[stage] || []).length === 0 && (
                  <div className="px-2 py-6 text-center text-xs text-muted-foreground">Empty</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {lostLead && (
        <LostLeadDialog lead={lostLead} onClose={() => setLostLead(null)} onSaved={load} />
      )}
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
